import { Form, Switch, Input, Select, Modal, Checkbox } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

import avatar from "assets/images/default.webp";
import {
  emailValidationRules,
  generalValidationRules,
  nameInputValidationRules,
  phoneNumberValidationRule,
} from "utils/formHelpers/validations";
import {
  QUERY_KEY_FOR_TALENT_PROFILE,
  useGetTalentProfile,
} from "features/settings/hooks/useGetTalentProfile";
import { ChangeEvent, useEffect, useState } from "react";
import {
  GENDERS,
  COUNTRIES,
  LEVEL_LIST,
  SKILLS,
  LANGUAGES,
} from "constants/general";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { IUpdateTProfile, FileWithPreview } from "features/settings/types";
import { useUpdateTalentProfile } from "features/settings/hooks/useUpdateTalentProfile";
import { openNotification } from "utils/notification";
import { useQueryClient } from "react-query";
import TextArea from "antd/es/input/TextArea";
import { useSignIn } from "react-auth-kit";
import { resizeImageToSquare, resizeCoverPhoto } from "utils/resizephoto";
import * as pdfjs from "pdfjs-dist";
import { useGetSectors } from "admin/jobs/hooks/useGetSectors";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { routePath } from "config/routeMgt/routePath";
import ProfileSidebar from "./TalentProfileSidebar";
import { ProfileNavigationButtons } from "./ProfileNavigationButtons";
import { getNextSection } from "../../services/profileNavigationService";
import { useProfileCompletion } from "features/settings/hooks/useProfileCompletion"; // ADD THIS

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const CITIES_API_URL = "https://countriesnow.space/api/v0.1/countries/cities";
const DEFAULT_COVER = "/assets/cover_image.png";

export const TalentProfile = () => {
  const { data, isLoading: isGetTPLoading } = useGetTalentProfile();
  const { refetchProfile } = useProfileCompletion(); // ADD THIS
  const [form] = Form.useForm();
  const { token, userInfo } = useGetUserInfo();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mutate, isLoading: isUpdateTPLoading } = useUpdateTalentProfile();
  const [file, setFile] = useState<FileWithPreview | null>(null);
  const [resizedBlob, setResizedBlob] = useState<Blob | null>(null);
  const [coverPhotoBlob, setCoverPhotoBlob] = useState<Blob | null>(null);
  const [filteredCities, setFilteredCities] = useState<any[]>([]);
  const { data: sectorData, isLoading: loadingSector } = useGetSectors();
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [dualNationality, setDualNationality] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [active, setActive] = useState(() => {
    const path = window.location.pathname;
    if (path.includes("education")) return "Education";
    if (path.includes("experience")) return "Experience";
    return "Basic Details";
  });
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const signIn = useSignIn();

  const requiredRule = (label: string) => ({
    required: true,
    message: `${label} is required`,
  });

  useEffect(() => {
    if (data?.data) {
      const userCountry = data?.data.country;
      const languageArray = Array.isArray(data?.data.language)
        ? data?.data.language
        : [];
      const skillsarray = Array.isArray(data?.data.skills)
        ? data?.data.skills
        : [];

      setDualNationality(!!data?.data.dual_country);
      form.setFieldsValue({
        first_name: data?.data.first_name,
        middle_name: data?.data.middle_name,
        last_name: data?.data.last_name,
        email: data?.data.email,
        gender: data?.data.gender,
        executive_summary: data?.data.executive_summary,
        professional_goal: data?.data.professional_goal,
        phone: data?.data.phone,
        country: data?.data.country,
        city: data?.data.city,
        language: languageArray,
        prefered_industries: data?.data.prefered_industries,
        prefered_locations: data?.data.prefered_locations,
        linkedin: data?.data.linkedin,
        github: data?.data.github,
        behance: data?.data.behance,
        facebook: data?.data.facebook,
        x: data?.data.x,
        open_to_work: data?.data.open_to_work,
        relocate: data?.data.relocate,
        instagram: data?.data.instagram,
        current_location: data?.data.current_location,
        experience_level: data?.data.experience_level,
        current_position: data?.data.current_position,
        sector_id: data?.data.sector_id,
        skills: skillsarray,
      });

      if (userCountry) {
        setSelectedCountry(userCountry);
        fetchCities(userCountry);
      }

      setTimeout(() => {
        setIsDataLoaded(true);
      }, 100);
    }
  }, [form, data]);

  const phoneValidationRules = [
    { required: true, message: "Phone number is required" },
    {
      pattern:
        /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,3}[)]?[-\s\.]?[0-9]{3,4}[-\s\.]?[0-9]{3,4}$/,
      message: "Please enter a valid phone number",
    },
  ];

  const fetchCities = async (countryCode: string) => {
    try {
      const response = await fetch(CITIES_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          country: countryCode,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setFilteredCities(data.data);
      } else {
        console.error("Failed to fetch cities:", data);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
    const country = COUNTRIES.find((country) => country.name === value);
    if (country) {
      fetchCities(country.name);
    }
  };

  const handleSubmit = (
    formData: IUpdateTProfile,
    shouldNavigate: boolean = true,
  ) => {
    const props: IUpdateTProfile = {
      first_name: formData.first_name,
      middle_name: formData.middle_name,
      last_name: formData.last_name,
      gender: formData.gender,
      executive_summary: formData.executive_summary,
      photo: resizedBlob ? resizedBlob : null,
      cover_photo: coverPhotoBlob ? coverPhotoBlob : null,
      professional_goal: formData.professional_goal
        ? formData.professional_goal
        : "",
      phone: formData.phone ? formData.phone : "",
      country: formData.country ? formData.country : "",
      dual_country: dualNationality ? formData.dual_country : null,
      experience_level: formData.experience_level
        ? formData.experience_level
        : "",
      current_position: formData.current_position
        ? formData.current_position
        : "",
      sector_id: formData.sector_id ? formData.sector_id : "",
      city: formData.city ? formData.city : "",
      language: formData?.language,
      prefered_industries: formData.prefered_industries,
      prefered_locations: formData.prefered_locations,
      // Basic Details has no URL fields — preserve existing social URLs
      linkedin: data?.data?.linkedin ?? "",
      github: data?.data?.github ?? "",
      behance: data?.data?.behance ?? "",
      facebook: data?.data?.facebook ?? "",
      x: data?.data?.x ?? "",
      open_to_work: formData.open_to_work,
      relocate: formData.relocate,
      instagram: data?.data?.instagram ?? "",
      portfolio: data?.data?.portfolio ?? "",
      current_location: formData.current_location
        ? formData.current_location
        : "",
      skills: formData?.skills,
      token,
    };

    mutate(props, {
      onError: (err: any) => {
        let errorTitle = "Error";
        let errorDescription = "An error occurred, please try again.";

        if (err.response && err.response.data && err.response.data.error) {
          errorTitle = "Error: " + err.response.data.error;
          errorDescription =
            err.response.data.description || "Please try again.";
        }

        openNotification({
          title: errorTitle,
          state: "error",
          description: errorDescription,
          duration: 6,
        });
        setPendingSubmit(false);
      },
      onSuccess: (res: any) => {
        openNotification({
          title: "Success",
          state: "success",
          description: "Profile Update Successful",
          duration: 4.5,
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_FOR_TALENT_PROFILE],
        });
        refetchProfile();
        setPendingSubmit(false);
        setShowConfirmation(false);

        if (shouldNavigate) {
          const nextSection = getNextSection(window.location.pathname);
          if (nextSection) {
            navigate(nextSection.link);
          }
        }
      },
    });
  };

  const handleSaveClick = () => {
    form
      .validateFields()
      .then((values) => {
        setPendingSubmit(true);
        setShowConfirmation(true);
      })
      .catch((errorInfo) => {
        openNotification({
          title: "Validation Error",
          state: "error",
          description:
            "Please fill in all required fields correctly before saving.",
          duration: 4.5,
        });
        setPendingSubmit(false);
      });
  };

  const handleConfirmSubmit = () => {
    setShowConfirmation(false);
    form.submit();
  };

  const handleSaveAndNext = () => {
    form
      .validateFields()
      .then((values) => {
        setPendingSubmit(true);
        handleSubmit(values, true);
      })
      .catch((errorInfo) => {
        openNotification({
          title: "Validation Error",
          state: "error",
          description:
            "Please fill in all required fields correctly before saving.",
          duration: 4.5,
        });
        setPendingSubmit(false);
      });
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile({
        ...selectedFile,
        preview: URL.createObjectURL(selectedFile),
      } as FileWithPreview);
      try {
        const resizedImageBlob = await resizeImageToSquare(selectedFile, 200);
        setResizedBlob(resizedImageBlob);
      } catch (error) {
        console.error("Error resizing image:", error);
      }
    }
  };

  const coverPhotoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile({
        ...selectedFile,
        preview: URL.createObjectURL(selectedFile),
      } as FileWithPreview);
      try {
        const resizedImageBlob = await resizeCoverPhoto(selectedFile);
        setCoverPhotoBlob(resizedImageBlob);
      } catch (error) {
        console.error("Error resizing image:", error);
      }
    }
  };

  const handleFieldsChange = () => {
    if (isDataLoaded) {
      form
        .validateFields()
        .then(() => {
          setIsFormValid(true);
        })
        .catch(() => {
          setIsFormValid(false);
        });
    }
  };

  useEffect(() => {
    if (isDataLoaded) {
      form
        .validateFields()
        .then(() => {
          setIsFormValid(true);
        })
        .catch((errors) => {
          console.log("Initial validation failed:", errors);
          setIsFormValid(false);
        });
    }
  }, [isDataLoaded, form]);

  // }, [data]);

  return (
    <>
      <Form
        form={form}
        onFinish={(values) => handleSubmit(values, false)}
        layout="vertical"
        onValuesChange={handleFieldsChange}
      >
      
        <div className="flex justify-between mb-4">
          <div>
            <h4 className="font-semibold text-lg text-primary">
              Personal info
            </h4>
            <h3 className="text-sm">Update your details here.</h3>
          </div>
        </div>
        <hr />
        <div className="grid grid-cols-1 md:grid-cols-5 my-3 md:my-5 gap-3">
          <Form.Item noStyle name="image">
            <Input type="hidden" />
          </Form.Item>

          <div className="relative col-span-5">
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              id="fileInput"
              onChange={coverPhotoChange}
            />
            <i
              className="ri-edit-2-fill absolute top-2 right-2 p-2 bg-white rounded-full cursor-pointer text-primary hover:text-primary"
              onClick={() => document.getElementById("fileInput")?.click()}
            ></i>
            <img
              src={
                coverPhotoBlob
                  ? URL.createObjectURL(coverPhotoBlob)
                  : (data?.data.user.cover_photo || DEFAULT_COVER)
              }
              alt="cover"
              className="w-full h-full object-cover"
            />

            <div className="absolute -bottom-6 left-4">
              <div className="relative w-fit">
                <img
                  src={
                    resizedBlob
                      ? URL.createObjectURL(resizedBlob)
                      : (data?.data.dp ?? avatar)
                  }
                  alt="avatar"
                  className="h-20 w-20 rounded-full border-2 border-white shadow-lg"
                />

                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  id="cover_photo"
                  onChange={handleFileChange}
                />

                <i
                  className="ri-edit-2-fill absolute -top-1 -right-1 bg-white rounded-full p-1 shadow 
              cursor-pointer text-primary hover:text-primary"
                  onClick={() =>
                    document.getElementById("cover_photo")?.click()
                  }
                ></i>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <ProfileSidebar active={active} onSelect={setActive} />

          <div className="md:col-span-5 p-4 rounded shadow-lg w-full">
            <div className="bg-transparent">
              <div className="flex justify-between items-center w-full">
                <label className="font-medium">Open to work</label>
                <Form.Item
                  name="open_to_work"
                  valuePropName="checked"
                  className="mb-0"
                  noStyle
                >
                  <Switch
                    checkedChildren={<CheckOutlined />}
                    unCheckedChildren={<CloseOutlined />}
                  />
                </Form.Item>
              </div>
              <br />
              <h3 className="text-xs -mt-6 mb-3">
                You will be recommended to recruiters
              </h3>
            </div>

            <div className="bg-transparent">
              <div className="flex justify-between items-center w-full">
                <label className="font-medium">Open to relocate</label>
                <Form.Item name="relocate" valuePropName="checked" noStyle>
                  <Switch
                    checkedChildren={<CheckOutlined />}
                    unCheckedChildren={<CloseOutlined />}
                  />
                </Form.Item>
              </div>
            </div>

            <hr />

            <div className="flex justify-between mt-4">
              <div>
                <h3 className="font-medium">Upload your resume</h3>
                <h3 className="text-xs">
                  This will be attached to your profile for recruiters to view
                </h3>
              </div>
              <div className="relative">
                <Link
                  to={routePath.uploadResume}
                  className="px-4 py-2 bg-primary text-white rounded-md text-center hover:bg-primary-dark"
                >
                  Upload Resume
                </Link>
              </div>
            </div>
            <br />
            <hr />

            {/* Full Name Section */}
            <div className="grid grid-cols-1 md:grid-cols-5 mt-3 md:mt-5 gap-2">
              <h3>Full Name</h3>
              <div className="col-span-4">
                <div className="flex gap-x-3">
                  <Form.Item
                    name="first_name"
                    rules={[requiredRule("First Name")]}
                    validateTrigger="onBlur"
                    className="flex-1"
                  >
                    <Input placeholder="First name" />
                  </Form.Item>
                  <Form.Item name="middle_name" className="flex-1">
                    <Input placeholder="Middle Name (optional)" />
                  </Form.Item>
                  <Form.Item
                    name="last_name"
                    rules={[requiredRule("Last Name")]}
                    validateTrigger="onBlur"
                    className="flex-1"
                  >
                    <Input placeholder="Last name" />
                  </Form.Item>
                </div>
              </div>
            </div>

            <hr />

            {/* Email Section */}
            <div className="grid grid-cols-1 md:grid-cols-5 mt-3 md:mt-5 gap-2">
              <h3>Email Address</h3>
              <div className="col-span-4">
                <Form.Item
                  name="email"
                  rules={emailValidationRules}
                  className="mb-0"
                >
                  <Input placeholder="example@gmail.com" disabled />
                </Form.Item>
              </div>
            </div>

            <hr />

            {/* Phone Section */}
            <div className="grid grid-cols-1 md:grid-cols-5 mt-3 md:mt-5 gap-2">
              <h3>Phone Number</h3>
              <div className="col-span-4">
                <Form.Item
                  name="phone"
                  rules={phoneValidationRules}
                  validateTrigger="onBlur"
                  className="mb-0"
                >
                  <Input placeholder="+2348012345678" />
                </Form.Item>
              </div>
            </div>

            <hr />

            {/* Executive Summary */}
            <div className="grid grid-cols-1 md:grid-cols-5 mt-3 md:mt-5 gap-2">
              <h3>Executive Summary</h3>
              <div className="col-span-4">
                <Form.Item
                  name="executive_summary"
                  rules={[requiredRule("Executive Summary")]}
                  validateTrigger="onBlur"
                  className="mb-0"
                >
                  <TextArea rows={3} />
                </Form.Item>
              </div>
            </div>

            {/* Professional Goal */}
            <div className="grid grid-cols-1 md:grid-cols-5 mt-3 md:mt-5 gap-2">
              <h3>Professional Goal</h3>
              <div className="col-span-4">
                <Form.Item
                  name="professional_goal"
                  rules={[requiredRule("Professional Goal")]}
                  validateTrigger="onBlur"
                  className="mb-0"
                >
                  <TextArea rows={3} />
                </Form.Item>
              </div>
            </div>

            {/* Additional Fields Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-5">
              <div>
                <h3 className="mb-1">Current Role</h3>
                <Form.Item
                  name="current_position"
                  rules={[requiredRule("Current Role")]}
                  validateTrigger="onBlur"
                  className="mb-0"
                >
                  <Input placeholder="Current Role" />
                </Form.Item>
              </div>

              <div>
                <h3 className="mb-1">Experience Level</h3>
                <Form.Item
                  name="experience_level"
                  rules={[requiredRule("Experience Level")]}
                  validateTrigger="onBlur"
                  className="mb-0"
                >
                  <Select
                    options={LEVEL_LIST}
                    allowClear
                    placeholder="Select"
                  />
                </Form.Item>
              </div>

              <div>
                <h3 className="mb-1">City</h3>
                <Form.Item
                  name="city"
                  rules={[requiredRule("City")]}
                  validateTrigger="onBlur"
                  className="mb-0"
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="Select"
                    options={filteredCities.map((city) => ({
                      value: city,
                      label: city,
                    }))}
                  />
                </Form.Item>
              </div>

              <div>
                <h3 className="mb-1">Sector</h3>
                <Form.Item
                  name="sector_id"
                  rules={[requiredRule("Sector")]}
                  validateTrigger="onBlur"
                  className="mb-0"
                >
                  <Select
                    allowClear
                    showSearch
                    placeholder="Select"
                    loading={loadingSector}
                    optionFilterProp="label"
                    options={sectorData?.map((sector) => ({
                      value: sector.id,
                      label: sector.name,
                    }))}
                  />
                </Form.Item>
              </div>

              <div>
                <h3 className="mb-1">Current Location</h3>
                <Form.Item
                  name="current_location"
                  rules={generalValidationRules}
                  className="mb-0"
                >
                  <Input placeholder="Current Location" />
                </Form.Item>
              </div>

              <div>
                <h3 className="mb-1">Gender</h3>
                <Form.Item
                  name="gender"
                  rules={[requiredRule("Gender")]}
                  validateTrigger="onBlur"
                  className="mb-0"
                >
                  <Select options={GENDERS} allowClear placeholder="Select" />
                </Form.Item>
              </div>
            </div>

            {/* Nationality */}
            <div className="grid grid-cols-1 md:grid-cols-5 mt-3 md:mt-5 gap-2">
              <h3>Nationality</h3>
              <div className="col-span-2">
                <Form.Item
                  name="country"
                  rules={[requiredRule("Nationality")]}
                  validateTrigger="onBlur"
                  className="mb-0"
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="Select"
                    options={COUNTRIES?.map((country) => ({
                      value: country.name,
                      label: country.name,
                    }))}
                    onChange={handleCountryChange}
                  />
                </Form.Item>
              </div>
              <div className="col-span-2">
                <Form.Item
                  name="dual_nationality"
                  valuePropName="checked"
                  className="mb-0"
                >
                  <Checkbox
                    onChange={(e) => setDualNationality(e.target.checked)}
                  >
                    Dual Nationality
                  </Checkbox>
                </Form.Item>
              </div>
            </div>

            {/* Second Nationality (conditional) */}
            {dualNationality && (
              <div className="grid grid-cols-1 md:grid-cols-5 mt-3 md:mt-5 gap-2">
                <h3>Second Nationality</h3>
                <div className="col-span-2">
                  <Form.Item
                    name="dual_country"
                    rules={[requiredRule("Second Nationality")]}
                    validateTrigger="onBlur"
                    className="mb-0"
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select second country"
                      options={COUNTRIES?.map((country) => ({
                        value: country.name,
                        label: country.name,
                      }))}
                    />
                  </Form.Item>
                </div>
              </div>
            )}

            {/* Skills */}
            <div className="grid grid-cols-1 md:grid-cols-5 mt-3 md:mt-5 gap-2">
              <h3>Skills & Competencies</h3>
              <div className="col-span-4">
                <Form.Item
                  name="skills"
                  rules={[requiredRule("Skills & Competencies")]}
                  validateTrigger="onBlur"
                  className="mb-0"
                >
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder="Select skills"
                    options={SKILLS}
                  />
                </Form.Item>
              </div>
            </div>

            <br />
            <hr />

            <div className="mt-6">
              <ProfileNavigationButtons
                onSave={handleSaveAndNext}
                isLoading={isUpdateTPLoading}
                isSubmitting={pendingSubmit}
                hasForm={true}
                nextButtonLabel="Next"
              />
            </div>

            <br />
            <hr />

            {/* Confirmation Modal */}
            <Modal
              title="Please Review Your Information"
              open={showConfirmation}
              onOk={handleConfirmSubmit}
              onCancel={() => {
                setShowConfirmation(false);
                setPendingSubmit(false);
              }}
              okText="Yes, Save Changes"
              cancelText="Review Again"
              centered
            >
              <div className="p-4">
                <div className="flex items-start gap-3 mb-4">
                  <i className="ri-error-warning-line text-yellow-500 text-2xl mt-1"></i>
                  <div>
                    <p className="font-medium mb-1">
                      Are you sure you want to save your profile?
                    </p>
                    <p className="text-sm">
                      Please verify that all your information is accurate and
                      complete before saving. Pay special attention to:
                    </p>
                    <ul className="text-sm list-disc pl-5 mt-2">
                      <li>Contact details (email, phone)</li>
                      <li>Professional information (role, experience level)</li>
                      <li>Skills and competencies</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Modal>
          </div>
        </div>
      </Form>
    </>
  );
};
